
(function() {
    'use strict';

    // ============================================================================
    // CONFIGURATION
    // ============================================================================
    
    const DEFAULT_CONFIG = {
        webhook: {
            url: '',
            route: ''
        },
        branding: {
            logo: '',
            name: 'Support Chat',
            welcomeText: 'Hi 👋, how can we help?',
            responseTimeText: 'We typically respond right away'
        },
        style: {
            primaryColor: '#854fff',
            secondaryColor: '#6b3fd4',
            position: 'right',
            backgroundColor: '#fff',
            fontColor: '#854fff'
        },
        poweredBy: {
            show: true,
            text: 'Powered by ChatBot',
            link: 'https://dgs.ai'
        },
        showWelcomeScreen: true,
        sendButtonText: 'Send',
        inputPlaceholder: 'Type your message...',
        typingText: 'Typing...',
        autoInit: {
            enabled: false,
            delay: 3,
            hideAfterClose: 0
        },
        mobile: {
            enablePopupNotification: true,
            popupDuration: 5000,
            fullScreenChat: true
        },
        animation: {
            enablePulse: true,
            pulseDuration: 2
        },
        typing: {
            minDelay: 1000,    // Минимальная задержка перед показом "Typing..." (1 сек)
            maxDelay: 3000,    // Максимальная задержка (3 сек)
            charDelay: 50      // Дополнительная задержка на символ (50 мс)
        }
    };

    // ============================================================================
    // UTILITY FUNCTIONS
    // ============================================================================
    
    const Utils = {
        isObject(item) {
            return item && typeof item === 'object' && !Array.isArray(item);
        },

        deepMerge(target, source) {
            const output = Object.assign({}, target);
            if (Utils.isObject(target) && Utils.isObject(source)) {
                Object.keys(source).forEach(key => {
                    if (Utils.isObject(source[key])) {
                        if (!(key in target)) {
                            Object.assign(output, { [key]: source[key] });
                        } else {
                            output[key] = Utils.deepMerge(target[key], source[key]);
                        }
                    } else {
                        Object.assign(output, { [key]: source[key] });
                    }
                });
            }
            return output;
        },

        detectMobile() {
            return window.innerWidth <= 768 || 
                   /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        },

        generateId() {
            // Use crypto.randomUUID() if available, otherwise fallback to custom implementation
            if (typeof crypto !== 'undefined' && crypto.randomUUID) {
                return crypto.randomUUID();
            }
            return 'conv_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
        },

        escapeHtml(text) {
            const map = {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#039;'
            };
            return text.replace(/[&<>"']/g, (m) => map[m]);
        }
    };

    // ============================================================================
    // ANALYTICS MANAGER
    // ============================================================================
    
    const AnalyticsManager = {
        // Отправка событий в различные аналитические системы
        trackEvent(eventName, eventData = {}) {
            // Google Tag Manager (dataLayer)
            if (window.dataLayer) {
                window.dataLayer.push({
                    'event': eventName,
                    'chat_widget_data': eventData
                });
            }
            
            // Google Analytics 4
            if (window.gtag) {
                window.gtag('event', eventName, {
                    custom_parameters: eventData
                });
            }
            
            // Facebook Pixel
            if (window.fbq) {
                window.fbq('track', eventName, eventData);
            }
            
            // Вызов пользовательских функций
            this.callCustomFunctions(eventName, eventData);
        },
        
        // Вызов пользовательских функций аналитики
        callCustomFunctions(eventName, eventData) {
            const functionName = `chat_${eventName}`;
            
            if (typeof window[functionName] === 'function') {
                try {
                    window[functionName](eventData);
                } catch (error) {
                    // Silent error handling
                }
            }
        },
        
        // Отслеживание первого сообщения
        trackFirstMessage(messageData) {
            this.trackEvent('onMessageSent', {
                type: 'first_message',
                message: messageData.message,
                timestamp: new Date().toISOString(),
                session_id: messageData.sessionId,
                utm_data: messageData.utmData
            });
        },
        
        // Отслеживание отправки контактов
        trackContactSent(contactData) {
            this.trackEvent('onSendContact', {
                type: 'contact_sent',
                contact_info: contactData.contactInfo,
                message: contactData.message,
                timestamp: new Date().toISOString(),
                session_id: contactData.sessionId,
                utm_data: contactData.utmData
            });
        },
        
        // Отслеживание открытия чата
        trackChatOpened(openData) {
            this.trackEvent('onChatOpened', {
                type: 'chat_opened',
                trigger: openData.trigger, // 'auto', 'manual', 'popup'
                timestamp: new Date().toISOString(),
                session_id: openData.sessionId,
                utm_data: openData.utmData
            });
        },
        
        // Отслеживание начала разговора
        trackConversationStarted(startData) {
            this.trackEvent('onConversationStarted', {
                type: 'conversation_started',
                timestamp: new Date().toISOString(),
                session_id: startData.sessionId,
                utm_data: startData.utmData
            });
        }
    };
    
    // ============================================================================
    // STORAGE MANAGER
    // ============================================================================
    
    class StorageManager {
        static KEYS = {
            WELCOME_SHOWN: 'chatWidget_welcome_shown',
            LAST_PAGE: 'chatWidget_last_page',
            SESSION_ID: 'chatWidget_sessionId',
            BLOCKED_UNTIL: 'chatWidget_blocked_until'
        };

        static getWelcomeShown() {
            const lastShown = localStorage.getItem(this.KEYS.WELCOME_SHOWN);
            if (!lastShown) return false;
            
            const timeSinceLastShown = Date.now() - parseInt(lastShown);
            const hideAfterCloseMs = (window.ChatWidgetConfig?.autoInit?.hideAfterClose || 0) * 60 * 1000;
            
            if (timeSinceLastShown >= hideAfterCloseMs) {
                this.clearWelcomeShown();
                return false;
            }
            
            return true;
        }

        static markWelcomeShown() {
            localStorage.setItem(this.KEYS.WELCOME_SHOWN, Date.now().toString());
        }

        static clearWelcomeShown() {
            localStorage.removeItem(this.KEYS.WELCOME_SHOWN);
            sessionStorage.removeItem(this.KEYS.LAST_PAGE);
        }

        static getLastPageData() {
            const data = sessionStorage.getItem(this.KEYS.LAST_PAGE);
            if (!data) return null;
            
            try {
                return JSON.parse(data);
            } catch (e) {
                this.clearLastPageData();
                return null;
            }
        }

        static savePageData(timeSpent, wasOpened) {
            const pageData = {
                timeSpent,
                wasOpened,
                timestamp: Date.now()
            };
            sessionStorage.setItem(this.KEYS.LAST_PAGE, JSON.stringify(pageData));
        }

        static clearLastPageData() {
            sessionStorage.removeItem(this.KEYS.LAST_PAGE);
        }

        static getSessionId() {
            let sessionId = sessionStorage.getItem(this.KEYS.SESSION_ID);
            if (!sessionId) {
                sessionId = 'session_' + Utils.generateId();
                sessionStorage.setItem(this.KEYS.SESSION_ID, sessionId);
            }
            return sessionId;
        }

        static getBlockedUntil() {
            return localStorage.getItem(this.KEYS.BLOCKED_UNTIL);
        }

        static setBlockedUntil(timestamp) {
            localStorage.setItem(this.KEYS.BLOCKED_UNTIL, timestamp.toString());
        }

        static clearBlockedUntil() {
            localStorage.removeItem(this.KEYS.BLOCKED_UNTIL);
        }

        static cleanupOldData() {
            const lastPageData = this.getLastPageData();
            if (lastPageData) {
                const maxAge = 5 * 60 * 1000; // 5 minutes
                if (Date.now() - lastPageData.timestamp > maxAge) {
                    this.clearLastPageData();
                }
            }
            
            // Clean up old conversation histories (older than 7 days)
            const maxHistoryAge = 7 * 24 * 60 * 60 * 1000; // 7 days
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith('chat_history_')) {
                    try {
                        const history = JSON.parse(sessionStorage.getItem(key));
                        if (Date.now() - history.timestamp > maxHistoryAge) {
                            sessionStorage.removeItem(key);
                        }
                    } catch (e) {
                        // Remove invalid entries
                        sessionStorage.removeItem(key);
                    }
                }
            }
        }
    }

    // ============================================================================
    // STYLE MANAGER
    // ============================================================================
    
    class StyleManager {
        constructor(config) {
            this.config = config;
        }

        injectStyles() {
            if (document.getElementById('chat-widget-styles')) return;
            
            const style = document.createElement('style');
            style.id = 'chat-widget-styles';
            style.textContent = this.getWidgetStyles();
            document.head.appendChild(style);
        }

        getWidgetContainerStyles() {
            const position = this.config.style.position === 'left' ? 'left: 20px;' : 'right: 20px;';
            return `
                position: fixed;
                bottom: calc(20px + env(safe-area-inset-bottom));
                ${position}
                z-index: 10000;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            `;
        }

        getToggleButtonStyles() {
            return `
                width: 60px;
                height: 60px;
                border: none;
                border-radius: 50%;
                background: ${this.config.style.primaryColor};
                color: white;
                font-size: 24px;
                cursor: pointer;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                transition: all 0.3s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                outline: none;
                position: relative;
                z-index: 2;
            `;
        }

        getPulseContainerStyles() {
            const pulseAnimation = this.config.animation.enablePulse 
                ? `animation: chatPulseGlow ${this.config.animation.pulseDuration}s ease-in-out infinite;` 
                : '';
            
            return `
                position: absolute;
                width: 60px;
                height: 60px;
                border-radius: 50%;
                background: ${this.config.style.primaryColor};
                opacity: 0.3;
                z-index: 1;
                pointer-events: none;
                top: 0;
                left: 0;
                ${pulseAnimation}
            `;
        }

        getChatWindowStyles() {
            const position = this.config.style.position === 'left' ? 'left: 0;' : 'right: 0;';
            return `
                position: absolute;
                bottom: 80px;
                ${position}
                width: 350px;
                height: 500px;
                background: ${this.config.style.backgroundColor};
                border-radius: 12px;
                box-shadow: 0 10px 30px rgba(0,0,0,0.2);
                display: flex;
                flex-direction: column;
                overflow: hidden;
                opacity: 0;
                transform: scale(0.8);
                transition: all 0.3s ease;
            `;
        }

        getNotificationStyles() {
            const position = this.config.style.position === 'left' ? 'left: 20px;' : 'right: 20px;';
            return `
                position: fixed;
                top: 20px;
                ${position}
                max-width: 300px;
                padding: 12px 16px;
                border-radius: 8px;
                color: white;
                font-size: 14px;
                z-index: 10001;
                transform: translateX(100%);
                transition: transform 0.3s ease;
                display: none;
            `;
        }

        getMobilePopupStyles() {
            const position = this.config.style.position === 'left' ? 'left: 20px;' : 'right: 20px;';
            return `
                position: fixed;
                bottom: 100px;
                ${position}
                max-width: 300px;
                background: white;
                border-radius: 16px;
                box-shadow: 0 8px 32px rgba(0,0,0,0.15);
                z-index: 10001;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                animation: popupSlideIn 0.3s ease-out;
                cursor: pointer;
                border: 1px solid #e0e0e0;
                transform: translateY(0);
                opacity: 1;
            `;
        }

        getWidgetStyles() {
            return `
                /* Support for safe areas and viewport */
                @supports (padding: max(0px)) {
                    #chat-widget-container {
                        padding-bottom: max(20px, env(safe-area-inset-bottom));
                    }
                }
                
                #chat-widget-container .chat-header {
                    background: ${this.config.style.primaryColor};
                    color: white;
                    padding: 16px;
                    padding-top: calc(16px + env(safe-area-inset-top));
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                #chat-widget-container .chat-header-content {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                
                #chat-widget-container .chat-logo {
                    width: 35px;
                    height: 35px;
                    border-radius: 50%;
                }
                
                #chat-widget-container .chat-title {
                    font-weight: 600;
                    font-size: 16px;
                }
                
                #chat-widget-container .chat-close {
                    background: none;
                    border: none;
                    color: white;
                    font-size: 24px;
                    cursor: pointer;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    transition: background 0.2s;
                }
                
                #chat-widget-container .chat-close:hover {
                    background: rgba(255,255,255,0.2);
                }
                
                #chat-widget-container .chat-body {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }
                
                #chat-widget-container .new-conversation {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 30px;
                    text-align: center;
                    background: linear-gradient(135deg, #f8f9ff, #e8f4ff);
                }
                
                #chat-widget-container .welcome-content h3 {
                    color: ${this.config.style.fontColor};
                    margin-bottom: 12px;
                    font-size: 18px;
                }
                
                #chat-widget-container .welcome-content p {
                    color: #666;
                    margin-bottom: 24px;
                    line-height: 1.5;
                }
                
                #chat-widget-container .start-conversation-btn {
                    background: ${this.config.style.primaryColor};
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 25px;
                    cursor: pointer;
                    font-weight: 600;
                    transition: all 0.2s;
                }
                
                #chat-widget-container .start-conversation-btn:hover {
                    background: ${this.config.style.secondaryColor};
                    transform: translateY(-1px);
                }
                
                #chat-widget-container .chat-messages {
                    flex: 1;
                    padding: 16px;
                    overflow-y: auto;
                    background: #f8f9fa;
                }
                
                #chat-widget-container .message {
                    margin-bottom: 12px;
                    display: flex;
                }
                
                #chat-widget-container .message-content {
                    max-width: 80%;
                    padding: 10px 14px;
                    border-radius: 18px;
                    word-wrap: break-word;
                    line-height: 1.4;
                }
                
                #chat-widget-container .user-message {
                    justify-content: flex-end;
                }
                
                #chat-widget-container .user-message .message-content {
                    background: ${this.config.style.primaryColor};
                    color: white;
                    border-bottom-right-radius: 4px;
                }
                
                #chat-widget-container .bot-message .message-content {
                    background: white;
                    color: #333;
                    border: 1px solid #e0e0e0;
                    border-bottom-left-radius: 4px;
                    font-weight: 300;
                    font-size: medium;
                }
                
                #chat-widget-container .typing-indicator .message-content {
                    background: #e9ecef;
                    animation: pulse 1.5s ease-in-out infinite;
                }
                
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }

                @keyframes chatPulseGlow {
                    0% {
                        transform: scale(1);
                        opacity: 0.3;
                    }
                    50% {
                        transform: scale(1.4);
                        opacity: 0.1;
                    }
                    100% {
                        transform: scale(1);
                        opacity: 0.3;
                    }
                }
                
                #chat-widget-container .chat-input-container {
                    padding: 16px;
                    border-top: 1px solid #eee;
                    background: white;
                }
                
                #chat-widget-container .input-row {
                    display: flex;
                    gap: 8px;
                }
                
                #chat-widget-container .chat-input {
                    flex: 1;
                    padding: 12px 16px;
                    border: 1px solid #ddd;
                    border-radius: 25px;
                    font-size: 14px;
                    outline: none;
                    transition: border-color 0.2s;
                    resize: none;
                    font-family: inherit;
                    line-height: 1.4;
                    max-height: 120px;
                    overflow-y: auto;
                }
                
                #chat-widget-container .chat-input:focus {
                    border-color: ${this.config.style.primaryColor};
                }
                
                #chat-widget-container .send-button {
                    background: ${this.config.style.primaryColor};
                    color: white;
                    border: none;
                    padding: 12px 20px;
                    border-radius: 25px;
                    cursor: pointer;
                    font-weight: 600;
                    transition: all 0.2s;
                    white-space: nowrap;
                    font-size: medium;
                }
                
                #chat-widget-container .send-button:hover {
                    background: ${this.config.style.secondaryColor};
                    transform: translateY(-1px);
                }
                
                #chat-widget-container .powered-by {
                    padding: 12px 16px;
                    text-align: center;
                    border-top: 1px solid #eee;
                    font-size: 12px;
                    background: #f8f9fa;
                }
                
                #chat-widget-container .powered-by a {
                    color: ${this.config.style.primaryColor};
                    text-decoration: none;
                    transition: opacity 0.2s;
                }
                
                #chat-widget-container .powered-by a:hover {
                    opacity: 0.8;
                }
                
                .chat-notification {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }
                
                .chat-notification.success {
                    background: #28a745;
                }
                
                .chat-notification.error {
                    background: #dc3545;
                }
                
                .chat-notification.info {
                    background: #17a2b8;
                }
                
                /* Mobile Popup Styles */
                @keyframes popupSlideIn {
                    from {
                        transform: translateY(50px);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }

                .mobile-chat-popup .popup-content {
                    display: flex;
                    align-items: center;
                    padding: 16px;
                    gap: 12px;
                    position: relative;
                }

                .mobile-chat-popup .popup-avatar {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: ${this.config.style.primaryColor};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 22px;
                    flex-shrink: 0;
                }

                .mobile-chat-popup .popup-avatar img {
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    object-fit: cover;
                }

                .mobile-chat-popup .popup-message {
                    flex: 1;
                }

                .mobile-chat-popup .popup-text {
                    font-weight: 600;
                    color: #333;
                    font-size: 16px;
                    margin-bottom: 4px;
                    line-height: 1.2;
                }

                .mobile-chat-popup .popup-subtext {
                    color: #666;
                    font-size: 14px;
                    line-height: 1.3;
                }

                .mobile-chat-popup .popup-close {
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    background: none;
                    border: none;
                    color: #999;
                    font-size: 18px;
                    cursor: pointer;
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    transition: all 0.2s;
                }

                .mobile-chat-popup .popup-close:hover {
                    background: #f0f0f0;
                    color: #666;
                }

                /* Mobile Full-Screen Chat */
                #chat-widget-container .mobile-fullscreen {
                    position: fixed !important;
                    top: 0 !important;
                    left: 0 !important;
                    right: 0 !important;
                    bottom: 0 !important;
                        width: 100vw !important;
                        height: 100vh !important;
                    border-radius: 0 !important;
                    z-index: 10000 !important;
                }

                @media (max-width: 768px) {
                    #chat-widget-container {
                        position: fixed !important;
                        bottom: 20px !important;
                        right: 20px !important;
                        z-index: 9999 !important;
                    }
                    
                    /* Support for devices with safe-area-inset */
                    @supports (padding: max(0px)) {
                        #chat-widget-container {
                            bottom: calc(20px + env(safe-area-inset-bottom)) !important;
                        }
                    }
                    
                    #chat-widget-container .mobile-fullscreen {
                        position: fixed !important;
                        top: 0 !important;
                        left: 0 !important;
                        right: 0 !important;
                        bottom: 0 !important;
                        width: 100vw !important;
                        height: 100vh !important;
                        border-radius: 0 !important;
                        z-index: 10000 !important;
                        display: flex !important;
                        flex-direction: column !important;
                    }
                    
                    #chat-widget-container .mobile-fullscreen .chat-header {
                        flex-shrink: 0 !important;
                        padding-top: calc(15px + env(safe-area-inset-top)) !important;
                    }
                    
                    /* Support for devices with safe-area-inset */
                    @supports (padding: max(0px)) {
                        #chat-widget-container .mobile-fullscreen .chat-header {
                            padding-top: calc(15px + env(safe-area-inset-top)) !important;
                        }
                    }
                    
                    /* Fallback for devices without safe-area support */
                    @supports not (padding: max(0px)) {
                        #chat-widget-container .mobile-fullscreen .chat-header {
                            padding-top: 25px !important;
                        }
                    }
                    
                    #chat-widget-container .mobile-fullscreen .chat-body {
                        flex: 1 !important;
                        overflow-y: auto !important;
                        padding-bottom: 80px !important;
                        margin-bottom: 0 !important;
                    }
                    
                    /* Support for devices with safe-area-inset */
                    @supports (padding: max(0px)) {
                        #chat-widget-container .mobile-fullscreen .chat-body {
                            padding-bottom: calc(80px + env(safe-area-inset-bottom)) !important;
                        }
                    }
                    
                    /* Fallback for devices without safe-area support */
                    @supports not (padding: max(0px)) {
                        #chat-widget-container .mobile-fullscreen .chat-body {
                            padding-bottom: 100px !important;
                        }
                    }
                    
                    #chat-widget-container .mobile-fullscreen .chat-input-container {
                        position: fixed !important;
                        bottom: 0 !important;
                        left: 0 !important;
                        right: 0 !important;
                        background: white !important;
                        border-top: 1px solid #eee !important;
                        padding: 16px !important;
                        z-index: 10001 !important;
                        min-height: 60px !important;
                    }
                    
                    /* Support for devices with safe-area-inset */
                    @supports (padding: max(0px)) {
                        #chat-widget-container .mobile-fullscreen .chat-input-container {
                            padding-bottom: calc(16px + env(safe-area-inset-bottom)) !important;
                            bottom: env(safe-area-inset-bottom) !important;
                        }
                    }
                    
                    /* Fallback for devices without safe-area support */
                    @supports not (padding: max(0px)) {
                        #chat-widget-container .mobile-fullscreen .chat-input-container {
                            padding-bottom: 32px !important;
                        }
                    }
                    
                    #chat-widget-toggle {
                        position: relative !important;
                        z-index: 2 !important;
                    }
                    
                    #chat-widget-pulse {
                        position: absolute !important;
                        top: 0 !important;
                        left: 0 !important;
                        z-index: 1 !important;
                    }
                    
                    /* Mobile popup with safe area support */
                    .mobile-chat-popup {
                        bottom: 100px !important;
                        pointer-events: auto !important;
                    }
                    
                    @supports (padding: max(0px)) {
                        .mobile-chat-popup {
                            bottom: calc(100px + env(safe-area-inset-bottom)) !important;
                        }
                    }
                    
                    /* Ensure chat stays open on mobile */
                    #chat-widget-container .mobile-fullscreen {
                        position: fixed !important;
                        top: 0 !important;
                        left: 0 !important;
                        right: 0 !important;
                        bottom: 0 !important;
                        width: 100vw !important;
                        height: 100vh !important;
                        height: 100dvh !important; /* Dynamic viewport height */
                        border-radius: 0 !important;
                        z-index: 10000 !important;
                        display: flex !important;
                        flex-direction: column !important;
                        opacity: 1 !important;
                        transform: scale(1) !important;
                    }
                }
            `;
        }
    }

    // ============================================================================
    // TEMPLATE MANAGER
    // ============================================================================
    
    class TemplateManager {
        constructor(config) {
            this.config = config;
        }

        getChatWindowHTML() {
            const welcomeScreenHTML = this.config.showWelcomeScreen ? this.getNewConversationHTML() : '';
            const messagesHTML = this.config.showWelcomeScreen ? '' : this.getMessagesHTML();
            const inputHTML = this.config.showWelcomeScreen ? '' : this.getInputHTML();
            const poweredByHTML = this.config.poweredBy.show ? this.getPoweredByHTML() : '';

            return `
                <div class="chat-header">
                    <div class="chat-header-content">
                        ${this.config.branding.logo ? `<img src="${this.config.branding.logo}" alt="Logo" class="chat-logo">` : ''}
                        <div class="chat-title">${this.config.branding.name}</div>
                    </div>
                    <button class="chat-close" id="chat-close-btn">×</button>
                </div>
                <div class="chat-body">
                    ${welcomeScreenHTML}
                    ${messagesHTML}
                </div>
                ${inputHTML}
                ${poweredByHTML}
            `;
        }

        getNewConversationHTML() {
            return `
                <div class="new-conversation" id="new-conversation">
                    <div class="welcome-content">
                        <div class="welcome-message">
                            <h3>${this.config.branding.welcomeText}</h3>
                            <p>${this.config.branding.responseTimeText}</p>
                        </div>
                        <button class="start-conversation-btn" id="start-conversation">Start New Conversation</button>
                    </div>
                </div>
            `;
        }

        getMessagesHTML() {
            return `
                <div class="chat-messages" id="chat-messages">
                    <div class="message bot-message">
                        <div class="message-content">${this.config.branding.welcomeText}</div>
                    </div>
                </div>
            `;
        }

        getInputHTML() {
            return `
                <div class="chat-input-container" id="chat-input-container">
                    <div class="input-row">
                        <textarea class="chat-input" id="chat-input" placeholder="${this.config.inputPlaceholder}" rows="1"></textarea>
                        <button class="send-button" id="send-button">${this.config.sendButtonText}</button>
                    </div>
                </div>
            `;
        }

        getPoweredByHTML() {
            return `
                <div class="powered-by">
                    <a href="${this.config.poweredBy.link}" target="_blank" rel="noopener">
                        ${this.config.poweredBy.text}
                    </a>
                </div>
            `;
        }

        getMobilePopupHTML() {
            return `
                <div class="popup-content">
                    <div class="popup-avatar">
                        ${this.config.branding.logo ? `<img src="${this.config.branding.logo}" alt="Avatar">` : '👋'}
                    </div>
                    <div class="popup-message">
                        <div class="popup-text">${this.config.branding.name}</div>
                        <div class="popup-subtext">${this.config.branding.welcomeText}</div>
                    </div>
                    <button class="popup-close" onclick="this.parentElement.parentElement.remove()">×</button>
                </div>
            `;
        }
    }

    // ============================================================================
    // MAIN CHAT WIDGET CLASS
    // ============================================================================
    
    class EnhancedChatWidget {
        constructor() {
            this.config = this.mergeConfig();
            this.styleManager = new StyleManager(this.config);
            this.templateManager = new TemplateManager(this.config);
            
            this.state = {
                isInitialized: false,
                isOpen: false,
                isChatStarted: false,
                isBlocked: false,
                isMobile: Utils.detectMobile(),
                mobilePopupShown: false,
                hasShownWelcome: StorageManager.getWelcomeShown(),
                pageStartTime: Date.now(),
                shouldShowOnNextPage: this.checkShouldShowOnNextPage(),
                isFirstMessage: true,
                hasSentContacts: false
            };
            

            
            this.timers = {
                autoInit: null
            };
            
            this.data = {
                conversationId: null,
                messages: [],
                utmData: this.extractUTMParams(),
                analyticsEvents: []
            };
            
            this.elements = {
                widgetContainer: null,
                toggleButton: null,
                pulseContainer: null,
                chatWindow: null
            };
            
            this.init();
        }

        // ============================================================================
        // INITIALIZATION
        // ============================================================================
        
        mergeConfig() {
            const userConfig = window.ChatWidgetConfig || {};
            return Utils.deepMerge(DEFAULT_CONFIG, userConfig);
        }
        
        extractUTMParams() {
            const urlParams = new URLSearchParams(window.location.search);
            const utmParams = {};
            
            // Собираем ВСЕ UTM метки
            const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'utm_name'];
            
            utmKeys.forEach(key => {
                const value = urlParams.get(key);
                if (value) {
                    utmParams[key] = value;
                }
            });
            
            // Также сохраняем referrer
            if (document.referrer) {
                utmParams.referrer = document.referrer;
            }
            
            return utmParams;
        }
        
        detectContacts(message) {
            const contactPatterns = {
                phone: [
                    // Украинские номера (все форматы)
                    /(\+?38)?0[0-9]{9}/g,                    // +380989999999, 380989999999, 0989999999
                    /(\+?38)?[0-9]{2}[0-9]{7}/g,             // +38999999999, 38999999999, 999999999
                    /0\s*8\s*0\s*0\s*[0-9]{2}\s*[0-9]{2}\s*[0-9]{2}/g,  // 0800 33 26 38
                    
                    // Международные форматы
                    /\+?[0-9]{1,3}[-.\s]?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{2}[-.\s]?[0-9]{2}/g,  // +1(234)567-8901
                    /\+?[0-9]{1,3}[-.\s]?[0-9]{3}[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}/g,                        // +1 234 567 8901
                    
                    // Американские форматы
                    /\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}/g,                                      // (123) 456-7890
                    /[0-9]{3}[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}/g,                                            // 123-456-7890
                    
                    // Европейские форматы
                    /\+?[0-9]{1,2}[-.\s]?[0-9]{2,4}[-.\s]?[0-9]{3,4}[-.\s]?[0-9]{3,4}/g,                  // +44 20 7946 0958
                    
                    // Короткие номера
                    /[0-9]{3,4}[-.\s]?[0-9]{3,4}/g,                                                        // 911, 112
                    
                    // Номера с пробелами и точками
                    /[0-9]{1,4}[\s.-]?[0-9]{1,4}[\s.-]?[0-9]{1,4}[\s.-]?[0-9]{1,4}/g,                     // 123 456 7890
                    
                    // Номера в скобках
                    /\([0-9]{1,4}\)[\s.-]?[0-9]{1,4}[\s.-]?[0-9]{1,4}/g,                                  // (123) 456-7890
                    
                    // Номера с расширениями
                    /[0-9]{1,4}[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,4}[\s]?[xX]?[0-9]{1,5}/g, // 123-456-7890 x123
                    
                    // Бесплатные номера
                    /[0-9]{3,4}[-.\s]?[0-9]{3,4}[-.\s]?[0-9]{3,4}/g,                                      // 800-555-1234
                    
                    // Мобильные номера разных стран
                    /\+?[0-9]{1,3}[-.\s]?[0-9]{4,5}[-.\s]?[0-9]{4,5}/g,                                   // +7 999 123-45-67
                    
                    // Номера с кодом страны без +
                    /[0-9]{1,3}[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,4}/g                       // 380 99 123 4567
                ],
                email: [
                    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
                ]
            };
            
            const contacts = {};
            
            Object.keys(contactPatterns).forEach(type => {
                contactPatterns[type].forEach(pattern => {
                    const matches = message.match(pattern);
                    if (matches) {
                        if (!contacts[type]) contacts[type] = [];
                        // Фильтруем дубликаты и слишком короткие номера
                        matches.forEach(match => {
                            const cleanMatch = match.replace(/[\s.-]/g, '');
                            if (cleanMatch.length >= 7 && !contacts[type].includes(match)) {
                                contacts[type].push(match);
                            }
                        });
                    }
                });
            });
            
            return Object.keys(contacts).length > 0 ? contacts : null;
        }

        init() {
            this.checkBlockStatus();
            this.styleManager.injectStyles();
            this.createWidget();
            this.bindEvents();
            
            StorageManager.cleanupOldData();
            this.setupMobileViewport();
            
            if (this.config.autoInit.enabled && !this.state.isBlocked) {
                this.scheduleAutoInit();
            }
            
            this.state.isInitialized = true;
        }

        // ============================================================================
        // WIDGET CREATION
        // ============================================================================
        
        createWidget() {
            this.createWidgetContainer();
            this.createToggleButton();
            this.createChatWindow();
        }

        createWidgetContainer() {
            this.elements.widgetContainer = document.createElement('div');
            this.elements.widgetContainer.id = 'chat-widget-container';
            this.elements.widgetContainer.style.cssText = this.styleManager.getWidgetContainerStyles();
            document.body.appendChild(this.elements.widgetContainer);
        }

        createToggleButton() {
            // Create pulse effect container
            this.elements.pulseContainer = document.createElement('div');
            this.elements.pulseContainer.id = 'chat-widget-pulse';
            this.elements.pulseContainer.style.cssText = this.styleManager.getPulseContainerStyles();
            
            // Create toggle button
            this.elements.toggleButton = document.createElement('button');
            this.elements.toggleButton.id = 'chat-widget-toggle';
            this.elements.toggleButton.innerHTML = '💬';
            this.elements.toggleButton.style.cssText = this.styleManager.getToggleButtonStyles();
            
            if (this.state.isBlocked) {
                this.elements.toggleButton.style.display = 'none';
                this.elements.pulseContainer.style.display = 'none';
            }
            
            // Add pulse container first, then button
            this.elements.widgetContainer.appendChild(this.elements.pulseContainer);
            this.elements.widgetContainer.appendChild(this.elements.toggleButton);
        }

        createChatWindow() {
            this.elements.chatWindow = document.createElement('div');
            this.elements.chatWindow.id = 'chat-widget-window';
            this.elements.chatWindow.style.cssText = this.styleManager.getChatWindowStyles();
            this.elements.chatWindow.style.display = 'none';
            
            this.elements.chatWindow.innerHTML = this.templateManager.getChatWindowHTML();
            this.elements.widgetContainer.appendChild(this.elements.chatWindow);
            
            this.applyDynamicContent();
        }

        applyDynamicContent() {
            const sendButton = this.elements.chatWindow.querySelector('#send-button');
            if (sendButton) {
                sendButton.textContent = this.config.sendButtonText;
            }
        }

        // ============================================================================
        // EVENT HANDLING
        // ============================================================================
        
        bindEvents() {
            this.bindToggleEvents();
            this.bindChatEvents();
            this.bindInputEvents();
            this.bindPageEvents();
        }

        bindToggleEvents() {
            this.elements.toggleButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggleWidget();
            });
        }

        bindChatEvents() {
            const closeBtn = this.elements.chatWindow.querySelector('#chat-close-btn');
            if (closeBtn) {
                closeBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.closeWidget();
                });
            }

            const startBtn = this.elements.chatWindow.querySelector('#start-conversation');
            if (startBtn) {
                startBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.startConversation();
                });
            }
        }

        bindInputEvents() {
            const sendBtn = this.elements.chatWindow.querySelector('#send-button');
            const input = this.elements.chatWindow.querySelector('#chat-input');
            
            if (sendBtn && input) {
                sendBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.sendMessage();
                });

                // Handle Enter and Ctrl/Cmd+Enter
                input.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') {
                        const isCtrlOrCmd = e.ctrlKey || e.metaKey;
                        
                        if (isCtrlOrCmd) {
                            // Ctrl/Cmd+Enter: Add new line
                            e.preventDefault();
                            const start = input.selectionStart;
                            const end = input.selectionEnd;
                            const value = input.value;
                            input.value = value.substring(0, start) + '\n' + value.substring(end);
                            input.selectionStart = input.selectionEnd = start + 1;
                            this.autoResizeTextarea(input);
                        } else if (!e.shiftKey) {
                            // Enter: Send message
                            e.preventDefault();
                            this.sendMessage();
                        }
                    }
                });

                // Auto-resize textarea
                input.addEventListener('input', () => {
                    this.autoResizeTextarea(input);
                });
            }
        }

        autoResizeTextarea(textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
        }

        bindPageEvents() {
            // Click outside to close
            document.addEventListener('click', (e) => {
                if (this.state.isOpen && !this.elements.widgetContainer.contains(e.target)) {
                    this.closeWidget();
                }
            });

            // Escape key to close
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.state.isOpen) {
                    this.closeWidget();
                }
            });

            // Save page data when user leaves
            window.addEventListener('beforeunload', () => {
                this.savePageData();
            });

            // Also save on page visibility change (mobile browsers)
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    this.savePageData();
                }
            });
        }

        // ============================================================================
        // WIDGET STATE MANAGEMENT
        // ============================================================================
        
        toggleWidget() {
            if (this.state.isBlocked) {
                this.showNotification('Chat is temporarily unavailable', 'error');
                return;
            }

            if (this.state.isOpen) {
                this.closeWidget();
            } else {
                this.openWidget();
            }
        }

        openWidget() {
            if (this.state.isBlocked) return;

            this.state.isOpen = true;
            this.state.shouldShowOnNextPage = false;
            
            this.hideMobilePopup();
            
            if (this.state.isMobile && this.config.mobile.fullScreenChat) {
                this.elements.chatWindow.classList.add('mobile-fullscreen');
                document.body.classList.add('chat-open');
            }
            
            this.elements.chatWindow.style.display = 'flex';
            this.elements.toggleButton.style.transform = 'rotate(180deg)';
            
            // Stop pulse animation when chat is open
            if (this.elements.pulseContainer) {
                this.elements.pulseContainer.style.animation = 'none';
            }
            
            this.clearAutoInitTimer();

            // Focus input if chat is started
            if (this.state.isChatStarted) {
                setTimeout(() => {
                    const input = this.elements.chatWindow.querySelector('#chat-input');
                    if (input) input.focus();
                }, 300);
            }

            // Animate opening
            setTimeout(() => {
                this.elements.chatWindow.style.opacity = '1';
                this.elements.chatWindow.style.transform = 'scale(1)';
                
                // Analytics: Track chat opened
                AnalyticsManager.trackChatOpened({
                    trigger: 'manual',
                    sessionId: this.data.conversationId,
                    utmData: this.data.utmData
                });
            }, 10);
        }

        closeWidget() {
            this.state.isOpen = false;
            this.elements.chatWindow.style.opacity = '0';
            this.elements.chatWindow.style.transform = 'scale(0.8)';
            this.elements.toggleButton.style.transform = 'rotate(0deg)';
            
            this.elements.chatWindow.classList.remove('mobile-fullscreen');
            document.body.classList.remove('chat-open');
            
            // Restart pulse animation when chat is closed
            if (this.config.animation.enablePulse && this.elements.pulseContainer) {
                this.elements.pulseContainer.style.animation = `chatPulseGlow ${this.config.animation.pulseDuration}s ease-in-out infinite`;
            }
            
            setTimeout(() => {
                this.elements.chatWindow.style.display = 'none';
            }, 300);
        }

        // ============================================================================
        // AUTO-INIT LOGIC
        // ============================================================================
        
        scheduleAutoInit() {
            // If we should show on this page based on previous page data
            if (this.state.shouldShowOnNextPage && !this.state.hasShownWelcome) {
                this.timers.autoInit = setTimeout(() => {
                    if (!this.state.isOpen && !this.state.isBlocked) {
                        this.showWelcomeMessage();
                    }
                }, this.config.autoInit.delay * 1000);
                return;
            }

            // Normal auto-init logic
            this.timers.autoInit = setTimeout(() => {
                if (!this.state.isOpen && !this.state.isBlocked && !this.state.hasShownWelcome) {
                    this.showWelcomeMessage();
                }
            }, this.config.autoInit.delay * 1000);
        }

        showWelcomeMessage() {
            if (this.state.isMobile && this.config.mobile.enablePopupNotification && !this.state.mobilePopupShown) {
                this.showMobilePopup();
                
                // Analytics: Track chat opened via popup
                AnalyticsManager.trackChatOpened({
                    trigger: 'popup',
                    sessionId: this.data.conversationId,
                    utmData: this.data.utmData
                });
            } else {
                this.openWidget();
                
                // Analytics: Track chat opened via auto-init
                AnalyticsManager.trackChatOpened({
                    trigger: 'auto',
                    sessionId: this.data.conversationId,
                    utmData: this.data.utmData
                });
            }
            this.markWelcomeShown();
        }

        clearAutoInitTimer() {
            if (this.timers.autoInit) {
                clearTimeout(this.timers.autoInit);
                this.timers.autoInit = null;
            }
        }

        // ============================================================================
        // MOBILE POPUP
        // ============================================================================
        
        showMobilePopup() {
            if (!this.state.isMobile || this.state.mobilePopupShown || this.state.hasShownWelcome) {
                return;
            }
            
            this.state.mobilePopupShown = true;
            this.state.shouldShowOnNextPage = false;
            
            const popup = document.createElement('div');
            popup.id = 'mobile-chat-popup';
            popup.className = 'mobile-chat-popup';
            popup.style.cssText = this.styleManager.getMobilePopupStyles();
            
            popup.innerHTML = this.templateManager.getMobilePopupHTML();
            
            // Add click handler to open chat
            popup.addEventListener('click', (e) => {
                if (!e.target.classList.contains('popup-close')) {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    // Remove popup immediately
                    if (popup.parentElement) {
                        popup.remove();
                    }
                    
                    // Open chat with a small delay to ensure popup is removed
                    setTimeout(() => {
                        this.openWidget();
                    }, 100);
                }
            });
            
            document.body.appendChild(popup);
            console.log('Mobile popup added to DOM');
            
            // Auto-hide after duration
            setTimeout(() => {
                if (popup.parentElement && !this.state.isOpen) {
                    popup.style.transform = 'translateY(100px)';
                    popup.style.opacity = '0';
                    setTimeout(() => {
                        if (popup.parentElement) {
                            popup.remove();
                        }
                    }, 300);
                }
            }, this.config.mobile.popupDuration);
            
            this.markWelcomeShown();
        }

        hideMobilePopup() {
            const popup = document.getElementById('mobile-chat-popup');
            if (popup) {
                popup.remove();
            }
        }

        // ============================================================================
        // CONVERSATION MANAGEMENT
        // ============================================================================
        
        async startConversation() {
            this.state.isChatStarted = true;
            
            const welcomeScreen = this.elements.chatWindow.querySelector('#new-conversation');
            const messagesContainer = this.elements.chatWindow.querySelector('.chat-body');
            const inputContainer = this.elements.chatWindow.querySelector('#chat-input-container');
            
            if (welcomeScreen) {
                welcomeScreen.style.display = 'none';
            }
            
            // Create messages area if not exists
            if (!this.elements.chatWindow.querySelector('#chat-messages')) {
                const messagesHTML = this.templateManager.getMessagesHTML();
                messagesContainer.innerHTML = messagesHTML;
            }
            
            // Create input area if not exists
            if (!inputContainer) {
                const inputHTML = this.templateManager.getInputHTML();
                const inputDiv = document.createElement('div');
                inputDiv.innerHTML = inputHTML;
                this.elements.chatWindow.appendChild(inputDiv.firstElementChild);
                
                this.bindSendEvents();
            }
            
            // Try to restore conversation history first
            const sessionId = StorageManager.getSessionId();
            const historyRestored = this.loadConversationHistory(sessionId);
            
            if (!historyRestored) {
                // Generate new session ID if no history found
                this.data.conversationId = Utils.generateId();
                
                // Load previous session if webhook is configured
                if (this.config.webhook.url) {
                    try {
                        await this.loadPreviousSession();
                                } catch (error) {
                // Continue with default welcome message
            }
                }
            }
            
            // Analytics: Track conversation started
            AnalyticsManager.trackConversationStarted({
                sessionId: this.data.conversationId,
                utmData: this.data.utmData
            });
            
            // Focus input
            setTimeout(() => {
                const input = this.elements.chatWindow.querySelector('#chat-input');
                if (input) input.focus();
            }, 100);
        }

        async loadPreviousSession() {
            const payload = {
                action: "loadPreviousSession",
                sessionId: this.data.conversationId,
                route: this.config.webhook.route,
                metadata: {
                    userId: StorageManager.getSessionId(),
                    utm_data: this.data.utmData,
                    session_data: {
                        isFirstMessage: this.state.isFirstMessage,
                        hasSentContacts: this.state.hasSentContacts,
                        isMobile: this.state.isMobile
                    }
                }
            };

            const response = await fetch(this.config.webhook.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            // Handle n8n response format for initial message
            let initialMessage = this.config.branding.welcomeText;
            if (Array.isArray(data)) {
                initialMessage = data[0]?.output || data[0]?.message || initialMessage;
            } else if (data && typeof data === 'object') {
                initialMessage = data.output || data.message || data.text || initialMessage;
            } else if (typeof data === 'string') {
                initialMessage = data;
            }
            
            // Update the initial message in the chat
            const messagesContainer = this.elements.chatWindow.querySelector('#chat-messages');
            if (messagesContainer) {
                const botMessageDiv = messagesContainer.querySelector('.bot-message .message-content');
                if (botMessageDiv) {
                    botMessageDiv.textContent = initialMessage;
                }
            }
            
            // Add to messages history
            this.data.messages.push({
                type: 'bot',
                content: initialMessage,
                timestamp: new Date().toISOString()
            });
        }

        // Save conversation history to storage
        saveConversationHistory() {
            if (this.data.conversationId && this.data.messages.length > 0) {
                const history = {
                    sessionId: this.data.conversationId,
                    messages: this.data.messages,
                    timestamp: Date.now()
                };
                sessionStorage.setItem(`chat_history_${this.data.conversationId}`, JSON.stringify(history));
            }
        }

        // Load conversation history from storage
        loadConversationHistory(sessionId) {
            const historyData = sessionStorage.getItem(`chat_history_${sessionId}`);
            if (historyData) {
                try {
                    const history = JSON.parse(historyData);
                    this.data.conversationId = history.sessionId;
                    this.data.messages = history.messages;
                    
                    // Restore messages in UI
                    this.restoreMessagesInUI();
                    return true;
                } catch (error) {
                    console.error('Error loading conversation history:', error);
                }
            }
            return false;
        }

        // Restore messages in UI
        restoreMessagesInUI() {
            const messagesContainer = this.elements.chatWindow.querySelector('#chat-messages');
            if (!messagesContainer) return;
            
            // Clear existing messages
            messagesContainer.innerHTML = '';
            
            // Add all messages from history
            this.data.messages.forEach(message => {
                this.addMessage(message.content, message.type);
            });
        }

        bindSendEvents() {
            const sendBtn = this.elements.chatWindow.querySelector('#send-button');
            const input = this.elements.chatWindow.querySelector('#chat-input');
            
            if (sendBtn && input) {
                sendBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.sendMessage();
                });

                // Handle Enter and Ctrl/Cmd+Enter
                input.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') {
                        const isCtrlOrCmd = e.ctrlKey || e.metaKey;
                        
                        if (isCtrlOrCmd) {
                            // Ctrl/Cmd+Enter: Add new line
                            e.preventDefault();
                            const start = input.selectionStart;
                            const end = input.selectionEnd;
                            const value = input.value;
                            input.value = value.substring(0, start) + '\n' + value.substring(end);
                            input.selectionStart = input.selectionEnd = start + 1;
                            this.autoResizeTextarea(input);
                        } else if (!e.shiftKey) {
                            // Enter: Send message
                            e.preventDefault();
                            this.sendMessage();
                        }
                    }
                });

                // Auto-resize textarea
                input.addEventListener('input', () => {
                    this.autoResizeTextarea(input);
                });
            }
        }

        async sendMessage() {
            const input = this.elements.chatWindow.querySelector('#chat-input');
            const message = input.value.trim();
            
            if (!message) return;
            
            this.addMessage(message, 'user');
            input.value = '';
            this.autoResizeTextarea(input);
            
            this.data.messages.push({
                type: 'user',
                content: message,
                timestamp: new Date().toISOString()
            });
            
            // Analytics: Track first message
            if (this.state.isFirstMessage) {
                AnalyticsManager.trackFirstMessage({
                    message: message,
                    sessionId: this.data.conversationId,
                    utmData: this.data.utmData
                });
                this.state.isFirstMessage = false;
            }
            
            // Analytics: Detect and track contacts
            const contacts = this.detectContacts(message);
            if (contacts && !this.state.hasSentContacts) {
                AnalyticsManager.trackContactSent({
                    contactInfo: contacts,
                    message: message,
                    sessionId: this.data.conversationId,
                    utmData: this.data.utmData
                });
                this.state.hasSentContacts = true;
            }
            
            // Save conversation history after user message
            this.saveConversationHistory();
            
            // Calculate typing delay based on message length
            const typingDelay = this.calculateTypingDelay(message);
            
            // Show typing indicator after delay
            setTimeout(() => {
                this.showTypingIndicator();
            }, typingDelay);
            
            try {
                const response = await this.sendToWebhook(message);
                this.hideTypingIndicator();
                
                // Handle n8n response format
                let botMessage = 'Thank you for your message!';
                if (Array.isArray(response)) {
                    botMessage = response[0]?.output || response[0]?.message || botMessage;
                } else if (response && typeof response === 'object') {
                    botMessage = response.output || response.message || response.text || botMessage;
                } else if (typeof response === 'string') {
                    botMessage = response;
                }
                
                this.addMessage(botMessage, 'bot');
                
                this.data.messages.push({
                    type: 'bot',
                    content: botMessage,
                    timestamp: new Date().toISOString()
                });
                
                // Analytics: Check for contact event in bot response
                if (response && typeof response === 'object' && response.event === 'send_contacts') {
                    AnalyticsManager.trackContactSent({
                        contactInfo: { type: 'operator_confirmed' },
                        message: botMessage,
                        sessionId: this.data.conversationId,
                        utmData: this.data.utmData
                    });
                    this.state.hasSentContacts = true;
                }
                
                // Save conversation history after bot response
                this.saveConversationHistory();
                
            } catch (error) {
                this.hideTypingIndicator();
                this.addMessage('Sorry, there was an error. Please try again.', 'bot');
            }
        }

        calculateTypingDelay(message) {
            const { minDelay, maxDelay, charDelay } = this.config.typing;
            const messageLength = message.length;
            const calculatedDelay = minDelay + (messageLength * charDelay);
            return Math.min(calculatedDelay, maxDelay);
        }

        async sendToWebhook(message) {
            if (!this.config.webhook.url) {
                throw new Error('Webhook URL is not configured');
            }

            const payload = {
                action: "sendMessage",
                sessionId: this.data.conversationId,
                route: this.config.webhook.route,
                chatInput: message,
                metadata: {
                    userId: StorageManager.getSessionId(),
                    timestamp: new Date().toISOString(),
                    utm_data: this.data.utmData,
                    session_data: {
                        isFirstMessage: this.state.isFirstMessage,
                        hasSentContacts: this.state.hasSentContacts,
                        isMobile: this.state.isMobile
                    }
                }
            };

            try {
                const response = await fetch(this.config.webhook.url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                
                // Validate response data
                if (!data) {
                    throw new Error('Empty response from webhook');
                }

                return data;
            } catch (error) {
                console.error('Webhook request failed:', error);
                throw error;
            }
        }

        addMessage(content, type) {
            const messagesContainer = this.elements.chatWindow.querySelector('#chat-messages');
            if (!messagesContainer) return;
            
            const messageDiv = document.createElement('div');
            messageDiv.className = `message ${type}-message`;
            messageDiv.innerHTML = `<div class="message-content">${Utils.escapeHtml(content)}</div>`;
            
            messagesContainer.appendChild(messageDiv);
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }

        showTypingIndicator() {
            const messagesContainer = this.elements.chatWindow.querySelector('#chat-messages');
            if (!messagesContainer) return;
            
            const typingDiv = document.createElement('div');
            typingDiv.className = 'message bot-message typing-indicator';
            typingDiv.id = 'typing-indicator';
            typingDiv.innerHTML = `<div class="message-content">${this.config.typingText}</div>`;
            
            messagesContainer.appendChild(typingDiv);
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }

        hideTypingIndicator() {
            const typingIndicator = this.elements.chatWindow.querySelector('#typing-indicator');
            if (typingIndicator) {
                typingIndicator.remove();
            }
        }

        // ============================================================================
        // STORAGE AND PERSISTENCE
        // ============================================================================
        
        checkShouldShowOnNextPage() {
            const lastPageData = StorageManager.getLastPageData();
            if (!lastPageData) return false;
            
            const timeSpent = lastPageData.timeSpent;
            const wasOpened = lastPageData.wasOpened;
            const timestamp = lastPageData.timestamp;
            
            // Check if data is not too old (max 5 minutes)
            const maxAge = 5 * 60 * 1000;
            if (Date.now() - timestamp > maxAge) {
                StorageManager.clearLastPageData();
                return false;
            }
            
            return timeSpent >= (this.config.autoInit.delay * 1000) && !wasOpened;
        }

        savePageData() {
            const timeSpent = Date.now() - this.state.pageStartTime;
            const wasOpened = this.state.isOpen || this.state.mobilePopupShown;
            StorageManager.savePageData(timeSpent, wasOpened);
        }

        markWelcomeShown() {
            StorageManager.markWelcomeShown();
            this.state.hasShownWelcome = true;
        }

        // ============================================================================
        // BLOCKING AND NOTIFICATIONS
        // ============================================================================
        
        checkBlockStatus() {
            const blockedUntil = StorageManager.getBlockedUntil();
            if (blockedUntil && Date.now() < parseInt(blockedUntil)) {
                this.state.isBlocked = true;
                const remainingTime = parseInt(blockedUntil) - Date.now();
                setTimeout(() => {
                    this.unblockWidget();
                }, remainingTime);
            } else {
                StorageManager.clearBlockedUntil();
            }
        }
        
        setupMobileViewport() {
            if (this.state.isMobile) {
                // Set viewport meta tag for better mobile experience
                let viewportMeta = document.querySelector('meta[name="viewport"]');
                if (viewportMeta) {
                    viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no');
                }
                
                // Add CSS to prevent browser UI from interfering
                const mobileViewportCSS = `
                    @media (max-width: 768px) {                        
                        body.chat-open {
                            position: fixed;
                            width: 100%;
                            height: 100%;
                            overflow: hidden;
                        }
                    }
                `;
                
                const style = document.createElement('style');
                style.textContent = mobileViewportCSS;
                document.head.appendChild(style);
            }
        }

        blockWidget() {
            const blockUntil = Date.now() + (this.config.autoInit.hideAfterClose * 60 * 1000);
            StorageManager.setBlockedUntil(blockUntil);
            
            this.state.isBlocked = true;
            this.elements.toggleButton.style.display = 'none';
            if (this.elements.pulseContainer) {
                this.elements.pulseContainer.style.display = 'none';
            }
            
            this.showNotification(`Chat hidden for ${this.config.autoInit.hideAfterClose} minutes`, 'info');
            
            setTimeout(() => {
                this.unblockWidget();
            }, this.config.autoInit.hideAfterClose * 60 * 1000);
        }

        unblockWidget() {
            StorageManager.clearBlockedUntil();
            this.state.isBlocked = false;
            this.elements.toggleButton.style.display = 'flex';
            if (this.elements.pulseContainer) {
                this.elements.pulseContainer.style.display = 'block';
            }
            this.showNotification('Chat is now available', 'success');
        }

        showNotification(message, type = 'info') {
            let notification = document.getElementById('chat-widget-notification');
            if (!notification) {
                notification = document.createElement('div');
                notification.id = 'chat-widget-notification';
                notification.style.cssText = this.styleManager.getNotificationStyles();
                document.body.appendChild(notification);
            }
            
            notification.textContent = message;
            notification.className = `chat-notification ${type}`;
            notification.style.display = 'block';
            notification.style.transform = 'translateX(0)';
            
            setTimeout(() => {
                notification.style.transform = 'translateX(100%)';
                setTimeout(() => {
                    notification.style.display = 'none';
                }, 300);
            }, 3000);
        }
    }

    // ============================================================================
    // INITIALIZATION
    // ============================================================================
    
    function initWidget() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                new EnhancedChatWidget();
            });
        } else {
            new EnhancedChatWidget();
        }
    }

    // Start initialization
    initWidget();

})();